import { useEffect, useState } from 'react'
import { Regimen } from '../../../../../types/Regimen'

interface RootCanalAdjustmentsProps {
  dentalProcedures: Regimen[]
  isSaved: boolean
  currentRootCanals: string
  rootCanalsTotal: string
  dentalItems: { [itemId: string]: string }
}

export const useRootCanalAdjustments = ({
  dentalProcedures,
  isSaved,
  currentRootCanals,
  rootCanalsTotal,
  dentalItems,
}: RootCanalAdjustmentsProps) => {
  const [adjustedRootCanals, setAdjustedRootCanals] =
    useState(currentRootCanals)
  const [adjustedTotalRootCanals, setAdjustedTotalRootCanals] =
    useState(rootCanalsTotal)
  const [hasRootCanalAdjustments, setHasRootCanalAdjustments] = useState(false)
  const [hasTotalRootCanalAdjustments, setHasTotalRootCanalAdjustments] =
    useState(false)

  useEffect(() => {
    if (isSaved && dentalProcedures.length > 0) {
      let current = Number(currentRootCanals) || 0
      let total = Number(rootCanalsTotal) || 0
      let hasAdjustment = false
      let hasTotalAdjustment = false

      // Process all dental procedures
      dentalProcedures.forEach((procedure) => {
        const lastUsage = procedure.usages[procedure.usages.length - 1]
        const procedureId = lastUsage?.fields?.dentalProcedureId
        const procedureName = dentalItems[procedureId]?.toLowerCase() || ''

        // Check for procedures that add root canals
        if (procedureName.includes('root canal placed')) {
          current += 1
          total += 1
          hasAdjustment = true
          hasTotalAdjustment = true
        }

        // Check for procedures that remove root canals
        if (
          procedureName.includes('root canal tooth pulled with amalgam') ||
          procedureName.includes('root canal tooth pulled without amalgam') ||
          (procedureName.includes('root canal') &&
            procedureName.includes('removal'))
        ) {
          current = Math.max(0, current - 1)
          hasAdjustment = true
        }
      })

      setAdjustedRootCanals(current.toString())
      setAdjustedTotalRootCanals(total.toString())
      setHasRootCanalAdjustments(hasAdjustment)
      setHasTotalRootCanalAdjustments(hasTotalAdjustment)
    } else {
      // If not saved or no procedures, use the original values
      setAdjustedRootCanals(currentRootCanals)
      setAdjustedTotalRootCanals(rootCanalsTotal)
      setHasRootCanalAdjustments(false)
      setHasTotalRootCanalAdjustments(false)
    }
  }, [
    dentalProcedures,
    isSaved,
    currentRootCanals,
    rootCanalsTotal,
    dentalItems,
  ])

  return {
    adjustedRootCanals,
    adjustedTotalRootCanals,
    hasRootCanalAdjustments,
    hasTotalRootCanalAdjustments,
  }
}

export default useRootCanalAdjustments
